import { Stack } from '@mui/material';

import type { FieldConfig } from '../config';
import type { SectionKey } from '../lease-details.types';
import { useLeaseDetailsUI } from '../lease-details-ui.context';
import { FieldDispatcher } from './field-dispatcher';

// Per-field dispatch layer. Internal — not re-exported from the package barrel.
// Section components hand it a `fields` map; it resolves rules against form
// state and dispatches each field by `kind` (or to a `component` override).

type FieldEntry = {
  name: string;
  field: FieldConfig;
};

export type FieldsRendererProps = {
  fields: Record<string, FieldConfig>;
  sectionKey: SectionKey;
  // When the renderer is mounted inside the applicants array editor, this is
  // the row index. Used to build dotted RHF paths like `applicants.0.email`.
  arrayIndex?: number;
  // True when an ancestor (e.g. the applicants primary-locked row) forces all
  // fields to render in the disabled state. OR'd with the field's own
  // `disabled` rule.
  inheritedDisabled?: boolean;
  // Names of per-entry fields that should render disabled in this row only —
  // used by the applicants section to express `primaryLocked: { fields: ... }`
  // (lock a subset of fields on the primary entry while leaving the rest
  // editable). OR'd with `inheritedDisabled` and the field's own `disabled`.
  inheritedDisabledFieldNames?: ReadonlySet<string>;
};

export const FieldsRenderer = ({
  fields,
  sectionKey,
  arrayIndex,
  inheritedDisabled = false,
  inheritedDisabledFieldNames,
}: FieldsRendererProps) => {
  const { spacing } = useLeaseDetailsUI();
  const entries: FieldEntry[] = Object.entries(fields).map(([name, field]) => ({ name, field }));

  // Collapse consecutive same-`rowGroup` fields into one row; everything else is
  // its own full-width row. Fields are spaced 16px (matches the legacy form).
  const rows: FieldEntry[][] = [];
  for (const entry of entries) {
    const group = entry.field.rowGroup;
    const previous = rows[rows.length - 1];
    if (group && previous && previous[0].field.rowGroup === group) {
      previous.push(entry);
    } else {
      rows.push([entry]);
    }
  }

  const renderField = ({ name, field }: FieldEntry) => (
    <FieldDispatcher
      key={name}
      name={name}
      field={field}
      sectionKey={sectionKey}
      arrayIndex={arrayIndex}
      inheritedDisabled={inheritedDisabled || (inheritedDisabledFieldNames?.has(name) ?? false)}
    />
  );

  const rowSpacing = spacing?.fields ?? 2;
  return (
    <Stack direction="column" spacing={rowSpacing}>
      {rows.map((row) =>
        row.length > 1 ? (
          <Stack
            key={row.map((entry) => entry.name).join('-')}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: rowSpacing, sm: 4 }}
            // hidden fields render `null`, so they leave no empty column.
            sx={{ '& > *': { flex: 1, minWidth: 0 } }}
          >
            {row.map(renderField)}
          </Stack>
        ) : (
          renderField(row[0])
        ),
      )}
    </Stack>
  );
};
